import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "../../../../explorer/hooks/useTranslation";
import { AddressDto } from "../../../../explorer/types/Basket/AddressDto";
import { OfferDetailsResponse } from "../../../../explorer/types/Basket/OfferDetailsResponse";
import { OfferPdfRequest } from "../../../../explorer/types/Basket/OfferPdfRequest";
import { OrderDetailsResponse } from "../../../../explorer/types/Basket/OrderDetailsResponse";
import ServiceProvider from "../../../configuration/ServiceProvider";
import { useApiNotification } from "../../../hook/useApiNotification";
import {
    AppIconsUnderCostruction,
    AppIconsSubmitted,
    AppIconsOrdered,
    AppIconsCancelled,
    AppIconsOrderShipped,
    AppIconsPdf,
    AppIconsMessage,
    AppIconsOrderTypeService,
    AppIconsZip,
    AppIconsOrderTypeSell,
} from "../../AppIcons";
import { OrderPdfRequest } from "../../../../explorer/types/Basket/OrderPdfRequest";
import { formatDate } from "../../../../explorer/helpers/CommonHelper";
import {
    ApiResponse,
    PdfResponse,
} from "../../../../explorer/types/Basket/PdfResponse";
import { useApiError } from "../../../hook/useApiError";
import { OrderShippingDocumentsRequest } from "../../../../explorer/types/Basket/OrderShippingDocumentsRequest";
import {
    OfferStatusType,
    OrderStatusType,
} from "../../../../explorer/types/Basket/OfferOrderStatusType";
import NoteIconComponent from "../../PartsList/TableLayout/TableColumnComponents/NoteIconComponent";
import OfferOrderStatusIconComponent from "../upload/OfferOrderStatusIconComponent";
import { OrderType } from "../../../../explorer/types/Basket/OrderType";
import useBrands from "../../../../explorer/hooks/useBrands";
import useCurrentSite from "../../../hook/useCurrentSite";
import { useTrackingOrderOffer } from "../../../hook/useTrackingOrderOffer";
import { OrderShippingDocumentsResponse } from "../../../../explorer/types/Basket/OrderShippingDocumentsResponse";
import FileSaver from "file-saver";
import { useFileDownload } from "../../../hook/useFileDownload";
import OfferOrderConditionsComponent from "../upload/OfferOrderConditionsComponent";
import AppIconsXls from "../../AppIcons/AppIconsXls";
import { StyleguideSavioColors } from "../../../configuration/themeSettings";

interface DetailSection {
    title: string;
    fields: Field[];
}

interface Field {
    label: string;
    value?: string | JSX.Element;
    icon?: JSX.Element;
    iconOnly?: boolean;
    rightComponent?: JSX.Element;
}

interface DetailsGridProps {
    details: OfferDetailsResponse | OrderDetailsResponse;
    type: "offer" | "order";
}

const DetailsGrid: React.FC<DetailsGridProps> = ({ details, type }) => {
    const { t } = useTranslation();
    const fileDownload = useFileDownload();

    const getAddress = (site: AddressDto): string => {
        if (!site?.address && !site?.city && !site?.zipCode && !site?.country)
            return "-";

        return `${site?.address} ${site?.address ? "-" : ""} ${site?.city}, ${
            site?.zipCode
        } (${site?.country})`.trim();
    };

    const OrderTypeIcon = (status: string | "") => {
        const enumValue = Object.values(OrderType).find(
            (value) => value === status
        );
        switch (enumValue) {
            case OrderType.Service:
                return <AppIconsOrderTypeService />;
            default:
                return <AppIconsOrderTypeSell/>;
        }
    };

    const getStatusIcon = (status: string, isOffer: boolean): JSX.Element => {
        if (isOffer) {
            const enumValue = Object.values(OfferStatusType).find(
                (value) => value === status
            );
            switch (enumValue) {
                case OfferStatusType.InPreparation:
                    return <AppIconsUnderCostruction />;
                case OfferStatusType.Processed:
                    return <AppIconsSubmitted />;
                case OfferStatusType.Ordered:
                    return <AppIconsOrdered />;
                case OfferStatusType.Cancelled:
                    return <AppIconsCancelled />;
                default:
                    return <></>;
            }
        } else {
            const enumValue = Object.values(OrderStatusType).find(
                (value) => value === status
            );
            switch (enumValue) {
                case OrderStatusType.InProcess:
                    return <AppIconsSubmitted />;
                case OrderStatusType.InPreparation:
                    return <AppIconsUnderCostruction />;
                case OrderStatusType.Shipped:
                    return <AppIconsOrdered />;
                case OrderStatusType.Cancelled:
                    return <AppIconsCancelled />;
                default:
                    return <></>;
            }
        }
    };

    const handlePdfDownload = async () => {
        try {
            let response: PdfResponse;

            if (type === "offer") {
                const requestData: OfferPdfRequest = {
                    sellingCompanyCode: brand.toUpperCase(),
                    customerCode: customerCode ?? "",
                    offerCode:
                        (details as OfferDetailsResponse).offerCode ||
                        (details as OfferDetailsResponse).customerOfferCode,
                };
                response = await ServiceProvider.OfferOrder.getOfferPdf(
                    requestData
                );
            } else {
                const requestData: OrderPdfRequest = {
                    sellingCompanyCode: brand.toUpperCase(),
                    customerCode: customerCode ?? "",
                    orderCode:
                        (details as OrderDetailsResponse).orderCode ||
                        (details as OrderDetailsResponse).customerOrderCode,
                    orderType: (details as OrderDetailsResponse).orderType,
                };

                response = await ServiceProvider.OfferOrder.getOrderPdf(
                    requestData
                );
            }

            if (response.hasError) {
                setError(response.message);
                return;
            }

            const pdfFile = response.pdfFile;
            const fileName = `${type}_${
                type === "offer"
                    ? (details as OfferDetailsResponse).offerCode
                    : (details as OrderDetailsResponse).orderCode
            }.pdf`;

            fileDownload.dowloadFile(pdfFile, "application/pdf", fileName);
            setNotification(t("common.pdfDownload.success"));
        } catch (e) {
            console.error("Error downloading PDF:", e);
            setError(t("common.pdfDownload.error"));
        }
    };

    const handleShippingDocsDownload = async () => {
        const requestData: OrderShippingDocumentsRequest = {
            sellingCompanyCode: brand.toUpperCase(),
            customerCode: customerCode ?? "",
            orderCode: (details as OrderDetailsResponse).orderCode,
            orderType: (details as OrderDetailsResponse).orderType,
        };

        ServiceProvider.OfferOrder.getOrderShippingDocuments(requestData)
            .then((response: OrderShippingDocumentsResponse) => {
                const zipFile = response.zipFile;
                const fileName = "shipping-documents.zip";

                fileDownload.dowloadFile(zipFile, "application/zip", fileName);
                setNotification(t("common.zipDownload.success"));
            })
            .catch((e) => {
                console.error("Error downloading ZIP: ", e);
                setError(t("common.zipDownload.error"));
            });
    };

    const [currentSite] = useCurrentSite();
    const [error, setError] = useApiError();
    const [notification, setNotification] = useApiNotification();
    const brand = useBrands().currentBrandUrl;
    const customerCode = currentSite.customerCode;
    const trackingOfferHook = useTrackingOrderOffer();

    const getDetailsIcons = (details: {
        pdfAvailable: boolean;
        shippingDocumentsAvailable?: boolean;
        note: string;
    }) => {
        return details.pdfAvailable ||
            details.shippingDocumentsAvailable ||
            details.note ? (
            <Box>
                {details.note && (
                    <Tooltip
                        children={<AppIconsMessage />}
                        title={details.note}
                        placement="top"
                    />
                )}
                {details.pdfAvailable && (
                    <Tooltip
                        title={t("common.downloadPdf")}
                        disableInteractive
                        placement="top"
                    >
                        <IconButton onClick={handlePdfDownload}>
                            <AppIconsPdf />
                        </IconButton>
                    </Tooltip>
                )}
                {details.shippingDocumentsAvailable && (
                    <Tooltip
                        title={t("common.downloadZip")}
                        disableInteractive
                        placement="top"
                    >
                        <IconButton onClick={handleShippingDocsDownload}>
                            <AppIconsZip />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        ) : undefined;
    };

    const buildSections = (
        details: OfferDetailsResponse | OrderDetailsResponse,
        type: "offer" | "order"
    ): DetailSection[] => {
        if (type !== "offer" && type !== "order") {
            throw new Error("Invalid type provided to buildSections");
        }

        const sectionsBuild: DetailSection[] = [];

        if (type === "offer") {
            const offerDetails = details as OfferDetailsResponse;
            sectionsBuild.push({
                title: "offerOrder.details",
                fields: [
                    {
                        label: t("offerOrder.customerOfferCode"),
                        value: offerDetails.customerOfferCode,
                        rightComponent: getDetailsIcons(offerDetails),
                    },
                    {
                        label: t("offerOrder.offerCode"),
                        value: offerDetails.offerCode,
                    },
                    {
                        label: t("common.spareparts.part.offerRequestDate"),
                        value: formatDate(offerDetails.offerRequestDate),
                    },
                    {
                        label: t("common.spareparts.part.validUntil"),
                        value: formatDate(offerDetails.offerExpirationDate),
                    },
                    {
                        label: t("common.spareparts.part.offerStatus"),
                        value: offerDetails.offerStatus,
                        //icon: getStatusIcon(offerDetails.offerStatus, true),
                    },
                    // offerDetails.pdfAvailable &&
                    // {
                    //     label: t("offerOrder.pdfAvailable"),
                    //     icon: (
                    //         <IconButton onClick={handlePdfDownload}>
                    //             <AppIconsPdf />
                    //         </IconButton>
                    //     ),
                    //     iconOnly: true,
                    // },
                    // offerDetails.note && {
                    //     label: t("orderoffer.note"),
                    //     icon: (
                    //         <Tooltip
                    //             children={<AppIconsMessage />}
                    //             title={offerDetails.note}
                    //             placement="top"
                    //         />
                    //     ),
                    //     iconOnly: true,
                    // },
                ].filter(Boolean) as Field[],
            });
        } else {
            const orderDetails = details as OrderDetailsResponse;
            sectionsBuild.push({
                title: "offerOrder.details",
                fields: [
                    {
                        label: t("offerOrder.customerOrderCode"),
                        value: orderDetails.customerOrderCode,
                        rightComponent: getDetailsIcons(orderDetails),
                    },
                    {
                        label: t("offerOrder.orderCode"),
                        value: orderDetails.orderCode,
                        //icon: OrderTypeIcon(orderDetails.orderType),
                    },

                    orderDetails.orderType === OrderType.Service && {
                        label: t("offerOrder.WarrantyOrderType"),
                        value: orderDetails.warrantyOrderType,
                    },
                    {
                        label: t("common.spareparts.part.orderDate"),
                        value: formatDate(orderDetails.orderDate),
                    },
                    {
                        label: t("common.spareparts.part.orderStatus"),
                        value: orderDetails.orderStatus,
                        //icon: getStatusIcon(orderDetails.orderStatus, false),
                    },
                    {
                        label: t("offerOrder.trackingNumber"),
                        value: orderDetails.trackingNumber,
                    },
                    // orderDetails.pdfAvailable && {
                    //     label: t("offerOrder.pdfAvailable"),
                    //     icon: (
                    //         <IconButton onClick={handlePdfDownload}>
                    //             <AppIconsPdf />
                    //         </IconButton>
                    //     ),
                    //     iconOnly: true,
                    // },
                    // orderDetails.shippingDocumentsAvailable && {
                    //     label: t("offerOrder.shippingDocuments"),
                    //     icon: (
                    //         <IconButton onClick={handleShippingDocsDownload}>
                    //             <AppIconsXls />
                    //         </IconButton>
                    //     ),
                    //     iconOnly: true,
                    // },
                    // orderDetails.note && {
                    //     label: t("orderoffer.note"),
                    //     icon: (
                    //         <Tooltip
                    //             children={<AppIconsMessage />}
                    //             title={orderDetails.note}
                    //             placement="top"
                    //         />
                    //     ),
                    //     iconOnly: true,
                    // },
                ].filter(Boolean) as Field[],
            });
        }

        // Seconda sezione: condizioni di vendita
        sectionsBuild.push({
            title: "common.salesCondition",
            fields: [
                {
                    label: t("common.salesConditions.carrier"),
                    value: details.carrier,
                },
                {
                    label: t("common.salesConditions.deliveryTerms"),
                    value: details.deliveryTerms,
                },
                {
                    label: t("common.salesConditions.paymentTerms"),
                    value: details.paymentTerms,
                },
                {
                    label: t("common.salesConditions.paymentMethod"),
                    value: details.paymentMethod,
                },
                {
                    label: t("common.salesConditions.currency"),
                    value: details.currency,
                },
            ],
        });

        // Terza sezione: indirizzo di spedizione
        sectionsBuild.push({
            title: "orderoffer.shipToAddress",
            fields: [
                {
                    label: t("offerOrder.customer"),
                    value: details.shipTo,
                },
                {
                    label: t("offerOrder.customerName"),
                    value: trackingOfferHook.getCustomerSiteNameByCode(
                        details.shipTo
                    ),
                },
                {
                    label: t("offerOrder.shipTo"),
                    value: trackingOfferHook.getCustomerAddressByCode(
                        details.shipTo
                    ),
                },
            ],
        });

        // Quarta sezione: indirizzo di fatturazione
        sectionsBuild.push({
            title: "orderoffer.invoicesAddress",
            fields: [
                {
                    label: t("offerOrder.customer"),
                    value: details.billTo,
                },
                {
                    label: t("offerOrder.customerName"),
                    value: trackingOfferHook.getCustomerSiteNameByCode(
                        details.billTo
                    ),
                },
                {
                    label: t("offerOrder.billTo"),
                    value: trackingOfferHook.getCustomerAddressByCode(
                        details.billTo
                    ),
                },
            ],
        });

        return sectionsBuild;
    };

    const sections = buildSections(details, type);

    // const shipTosite = trackingOfferHook.getSiteByCode(details.shipTo);
    // const billTosite = trackingOfferHook.getSiteByCode(details.billTo);

    const shipTosite = details.shipToDto;
    const billTosite = details.billToDto;

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",

                width: "100%",
                padding: 2,
            }}
        >
            {/* Prima colonna: prima sezione */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
                {/* <Typography variant="h6" gutterBottom>
                    {t(sections[0].title)}
                </Typography> */}
                {sections[0].fields
                    .filter((f) => f.icon || f.value || f.rightComponent)
                    .map((field, fieldIdx) => (
                        <Box
                            key={fieldIdx}
                            sx={{ display: "flex", alignItems: "baseline" }}
                        >
                            <Box sx={{ fontWeight: "bold" }}>
                                {field.label}:
                            </Box>
                            <Box
                                sx={{
                                    flex: 2,
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <div>
                                    {field.iconOnly ? (
                                        field.icon
                                    ) : (
                                        <>
                                            {field.icon && (
                                                <Box>{field.icon}</Box>
                                            )}
                                            {field.value && (
                                                <Box>{field.value}</Box>
                                            )}
                                        </>
                                    )}
                                </div>
                                {field.rightComponent && field.rightComponent}
                            </Box>
                        </Box>
                    ))}

                <Typography
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        color: StyleguideSavioColors.text.black + " !important",
                        svg: {
                            marginLeft: "10px",
                            color:
                                StyleguideSavioColors.mainBlue + " !important",
                        },
                    }}
                    variant="h6"
                    gutterBottom
                >
                    {t(sections[1].title)}{" "}
                    <OfferOrderConditionsComponent
                        item={details}
                    ></OfferOrderConditionsComponent>
                </Typography>
            </Box>
            {/* Terza colonna: divisa in due righe */}
            <Box sx={{ display: "grid", gridTemplateRows: "1fr 1fr" }}>
                {/* Parte superiore: terza sezione */}
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="h6" gutterBottom>
                        {t(sections[2].title)}
                    </Typography>
                    <Box>
                        {shipTosite && Object.keys(shipTosite).length > 0 && (
                            <>
                                {`${details.shipTo ?? ""} ${
                                    shipTosite?.customerSiteName ? `- ${shipTosite?.customerSiteName}` : ""}`}{" "}
                                <br />
                                {`${shipTosite?.address ?? ""} ${
                                    shipTosite?.address && "-"
                                }
                        ${shipTosite?.city ?? ""} ${
                                    shipTosite.zipCode ? `, ${shipTosite.zipCode}` : ""
                                } ${shipTosite?.country ? "(" + shipTosite?.country + ")" : ""}`}
                            </>
                        )}
                        {(!shipTosite ||
                            Object.keys(shipTosite).length === 0) && <>-</>}
                    </Box>
                </Box>

                {/* Parte inferiore: quarta sezione */}
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="h6" gutterBottom>
                        {t(sections[3].title)}
                    </Typography>
                    <Box>
                        {billTosite && Object.keys(billTosite).length > 0 && (
                            <>
                                {`${details.billTo ?? ""} ${
                                    billTosite?.customerSiteName ? `- ${billTosite?.customerSiteName}` : ""
                                }`}{" "}
                                <br />
                                {`${billTosite?.address ?? ""} ${
                                    billTosite?.address && "-"
                                }
                        ${billTosite?.city ?? ""} ${
                                    billTosite.zipCode ? `, ${billTosite.zipCode}` : ""
                                } ${billTosite?.country ? "(" + billTosite?.country + ")" : ""}`}
                            </>
                        )}
                        {(!billTosite ||
                            Object.keys(billTosite).length === 0) && <>-</>}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default DetailsGrid;
